import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { SimTypography } from 'components/SimTypography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { HashLink as Link } from 'react-router-hash-link';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronRightIcon } from '@radix-ui/react-icons';

const MenuBody = ({ children }) => {
	const theme = useTheme();

	return (
		<Box
			bgcolor={theme.palette.background.default}
			paddingX={2}
			paddingTop={4}
			paddingBottom={1}
			borderRadius={2}
			zIndex={3}>
			{children}
		</Box>
	);
};
const SubMenuBody = ({ children }) => {
	const theme = useTheme();

	return (
		<Box bgcolor={theme.palette.background.default} paddingX={3} zIndex={3} borderRadius={2}>
			{children}
		</Box>
	);
};

const MenuItemWithTrigger = ({ children, activeLink, item, href }) => {
	const theme = useTheme();

	return (
		<Box display='flex' alignItems='center'>
			<Button
				size='medium'
				height={'40px'}
				component={'a'}
				href={href}
				fullWidth
				sx={{
					justifyContent: 'flex-start',
					paddingLeft: item.isTabLink === 1 ? 2 : 0,
					color: activeLink === item.href ? theme.palette.primary.main : theme.palette.text.primary,
					backgroundColor: activeLink === item.href ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
					fontWeight: activeLink === item.href ? 600 : 400
				}}>
				<Link to={item.href} activeStyle={{ textDecoration: 'none' }}>
					<Box paddingLeft={2}>
						<SimTypography
							variant='menuRootText'
							lineHeight={'normal'}
							color={theme.palette.text.secondary}
							sx={{
								textDecoration: 'underline',
								textDecorationColor: alpha(theme.palette.background.default, 1)
							}}>
							{children}
						</SimTypography>
					</Box>
				</Link>
			</Button>
			<ChevronRightIcon />
		</Box>
	);
};

const SubMenuItem = ({ children, activeLink, item }) => {
	const theme = useTheme();
	const isActiveLink = activeLink === item.sectionAnchorLink;

	return (
		<Box>
			<Button
				size='medium'
				height={'40px'}
				margin={0}
				padding={0}
				component={'a'}
				fullWidth
				sx={{
					justifyContent: 'flex-start',
					color: isActiveLink ? theme.palette.primary.main : theme.palette.text.primary,
					backgroundColor: isActiveLink ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
					fontWeight: isActiveLink ? 600 : 400
				}}>
				<Link to={item.sectionAnchorLink}>
					<SimTypography
						variant='menuRootText'
						color={theme.palette.text.secondary}
						sx={{
							textDecoration: 'underline',
							textDecorationColor: alpha(theme.palette.background.default, 1)
						}}>
						{children}
					</SimTypography>
				</Link>
			</Button>
		</Box>
	);
};

const NavItemServices = ({ title, id, items, colorInvert = false }) => {
	const theme = useTheme();
	const [activeLink, setActiveLink] = useState('');
	const [openedPopoverId, setOpenedPopoverId] = useState(null);

	const handleClick = popoverId => {
		setOpenedPopoverId(popoverId);
	};

	const hasActiveLink = () => items.find(i => i.href === activeLink);
	const linkColor = colorInvert ? theme.palette.text.default : 'common.white';

	useEffect(() => {
		setActiveLink(window && window.location ? window.location.pathname : '');
	}, []);

	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger asChild style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
				<Box onClick={() => handleClick(id)}>
					<SimTypography
						fontWeight={openedPopoverId === id || hasActiveLink() ? 700 : 400}
						variant='menuRootText'
						color={linkColor}>
						{title}
					</SimTypography>
					<ExpandMoreIcon
						sx={{
							marginLeft: theme.spacing(1 / 4),
							width: 16,
							height: 16,
							transform: openedPopoverId === id ? 'rotate(180deg)' : 'none',
							color: linkColor
						}}
					/>
				</Box>
			</DropdownMenu.Trigger>

			<DropdownMenu.Portal>
				<DropdownMenu.Content className='DropdownMenuContent' sideOffset={5} style={{ zIndex: 10 }}>
					<MenuBody>
						{items.map((menu, idx) => (
							<Box key={idx}>
								<Box padding height={'40px'}>
									<SimTypography
										variant='menuRootText'
										lineHeight={'normal'}
										color={theme.palette.primary.main}>
										{menu.label}
									</SimTypography>
								</Box>

								{menu.body.map((subMenu, sIdx) => (
									<Box key={sIdx}>
										<DropdownMenu.Sub>
											<DropdownMenu.SubTrigger className='DropdownMenuSubTrigger'>
												<MenuItemWithTrigger
													activeLink={activeLink}
													item={subMenu}
													href={menu.body.href}>
													{subMenu.title}
												</MenuItemWithTrigger>
											</DropdownMenu.SubTrigger>
											<DropdownMenu.Portal>
												<DropdownMenu.SubContent
													className='DropdownMenuSubContent'
													sideOffset={2}
													alignOffset={-5}
													style={{ zIndex: 11 }}>
													<SubMenuBody>
														{subMenu.sections.map((subMenuItem, smIdx) => (
															<DropdownMenu.Item className='DropdownMenuItem' key={smIdx}>
																<SubMenuItem activeLink={activeLink} item={subMenuItem}>
																	{subMenuItem.sectionTitle}
																</SubMenuItem>
															</DropdownMenu.Item>
														))}
													</SubMenuBody>
												</DropdownMenu.SubContent>
											</DropdownMenu.Portal>
										</DropdownMenu.Sub>
									</Box>
								))}
							</Box>
						))}
					</MenuBody>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
};

NavItemServices.propTypes = {
	title: PropTypes.string.isRequired,
	id: PropTypes.string.isRequired,
	items: PropTypes.array.isRequired,
	colorInvert: PropTypes.bool
};

export default NavItemServices;
